import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Twitter, Linkedin, Link as LinkIcon, Facebook } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ShareBar = ({ title }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Copy failed. Please copy the URL manually.");
    }
  };
  const enc = encodeURIComponent;
  const cls = "w-9 h-9 border border-ink-line rounded-full flex items-center justify-center text-ink-soft hover:text-ink hover:border-ink transition-colors";
  return (
    <div className="flex items-center gap-2" data-testid="share-bar">
      <a href={`https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(window.location.href)}`} target="_blank" rel="noreferrer" data-testid="share-twitter" aria-label="Share on Twitter" className={cls}><Twitter size={14} strokeWidth={1.4}/></a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(window.location.href)}`} target="_blank" rel="noreferrer" data-testid="share-linkedin" aria-label="Share on LinkedIn" className={cls}><Linkedin size={14} strokeWidth={1.4}/></a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(window.location.href)}`} target="_blank" rel="noreferrer" data-testid="share-facebook" aria-label="Share on Facebook" className={cls}><Facebook size={14} strokeWidth={1.4}/></a>
      <button onClick={copy} data-testid="share-copy" aria-label="Copy link" className={cls}><LinkIcon size={14} strokeWidth={1.4}/></button>
    </div>
  );
};

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const a = await api.article(slug);
        setArticle(a);
        const all = await api.articles();
        const rel = all.filter((x) => x.slug !== slug && x.category === a.category).slice(0, 3);
        setRelated(rel.length ? rel : all.filter((x) => x.slug !== slug).slice(0, 3));
      } catch {
        setArticle({ notFound: true });
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!article || article.notFound) return;
    if (!Array.isArray(article.toc) || article.toc.length === 0) return;
    const handler = () => {
      const sections = article.toc.map((t) => document.getElementById(t.id)).filter(Boolean);
      const y = window.scrollY + 180;
      let current = sections[0]?.id;
      for (const s of sections) {
        if (s.offsetTop <= y) current = s.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [article]);

  if (!article) return <div className="min-h-[60vh] flex items-center justify-center text-ink-soft" data-testid="article-loading">Loading…</div>;
  if (article.notFound) return <div className="min-h-[60vh] flex items-center justify-center text-ink-soft">Article not found.</div>;

  return (
    <article data-testid="article-detail-page" className="pt-8 md:pt-14">
      {/* Hero */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <Link to="/articles" data-testid="article-back-link" className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-[11px] font-mono-editorial uppercase tracking-[0.22em]">
          <ArrowLeft size={14} /> Back to Articles
        </Link>
        <div className="mt-10 text-center">
          <div className="overline mb-6">{article.category}</div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="font-display font-normal text-ink text-4xl md:text-6xl lg:text-[72px] leading-[1.04] tracking-[-0.02em] max-w-5xl mx-auto"
          >
            {article.title}
          </motion.h1>
          {article.subtitle && (
            <p className="mt-6 text-ink-soft text-lg md:text-xl max-w-3xl mx-auto italic font-display">{article.subtitle}</p>
          )}
          <div className="mt-8 flex items-center justify-center gap-4 text-[11px] font-mono-editorial uppercase tracking-[0.18em] text-ink-muted">
            <span>{article.author}</span><span>·</span>
            <span>{new Date(article.date).toLocaleDateString(undefined,{year:"numeric",month:"long",day:"numeric"})}</span><span>·</span>
            <span>{article.read_time}</span>
          </div>
        </div>
      </div>

      <div className="mt-12 md:mt-16 max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="aspect-[16/9] md:aspect-[21/9] relative overflow-hidden bg-cream">
          <img src={article.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </div>

      {/* Body + TOC */}
      <div className="mt-16 md:mt-24 max-w-[1200px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="lg:sticky lg:top-28">
            {Array.isArray(article.toc) && article.toc.length > 0 && (
              <>
                <div className="overline mb-5">Table of Contents</div>
                <ul className="space-y-3" data-testid="article-toc">
                  {article.toc.map((t) => (
                    <li key={t.id}>
                      <a
                        href={`#${t.id}`}
                        data-testid={`toc-link-${t.id}`}
                        className={`text-sm block transition-colors ${activeId === t.id ? "text-ink font-medium" : "text-ink-soft hover:text-ink"}`}
                      >
                        <span className="mr-2 text-ink-muted font-mono-editorial">{t.id.replace("section-","")}.</span>{t.label}
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="hairline my-8" />
              </>
            )}
            <div className="overline mb-4">Share</div>
            <ShareBar title={article.title} />
          </div>
        </aside>

        <div className="lg:col-span-9 order-1 lg:order-2">
          <div className="prose-editorial max-w-[720px]">
            <p className="dropcap text-xl md:text-2xl leading-[1.55] text-ink">
              {article.excerpt}
            </p>
            {Array.isArray(article.content) && article.content.length > 0 ? (
              article.content.map((section) => (
                <section id={section.id} key={section.id} className="scroll-mt-28">
                  <h2>{section.heading}</h2>
                  {(section.paragraphs || []).map((p, i) => (<p key={i}>{p}</p>))}
                </section>
              ))
            ) : article.body_html ? (
              <div className="quill-body" dangerouslySetInnerHTML={{ __html: article.body_html }} />
            ) : null}
          </div>

          <div className="mt-16 pt-10 border-t border-ink-line flex flex-wrap items-center justify-between gap-6" data-testid="article-tags-share">
            <div className="flex flex-wrap gap-2">
              {(article.tags || []).map((t) => (
                <span key={t} className="text-[11px] font-mono-editorial uppercase tracking-[0.18em] px-3 py-1.5 border border-ink-line text-ink-soft rounded-full">{t}</span>
              ))}
            </div>
            <ShareBar title={article.title} />
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-24 md:mt-32 py-16 md:py-24 bg-cream border-y border-ink-line">
          <div className="max-w-[1200px] mx-auto px-5 md:px-8">
            <div className="overline overline-muted mb-4">Related Reading</div>
            <h3 className="font-display text-ink text-3xl md:text-4xl leading-tight mb-10">More from the desk.</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="article-related">
              {related.map((r) => (
                <Link key={r.id} to={`/articles/${r.slug}`} data-testid={`related-article-${r.slug}`} className="editorial-card p-6 group block">
                  <div className="overline overline-muted mb-3">{r.category}</div>
                  <div className="font-display text-ink text-xl leading-tight group-hover:text-gold-dark transition-colors">{r.title}</div>
                  <p className="mt-3 text-ink-soft text-sm line-clamp-3">{r.excerpt}</p>
                  <div className="mt-6 inline-flex items-center gap-1 text-ink text-xs">Read <ArrowUpRight size={12} /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default ArticleDetailPage;
